import React, { useEffect, useState } from "react";

import {
  CalendarDays,
  Clock3,
  CheckCircle2,
  RefreshCcw,
  User,
  Search,
  Bell,
  XCircle,
  Phone,
  FlaskConical,
  TestTube2,
  Home,
  Building2,
} from "lucide-react";

import API from "../api";
import Navbar from "./Navbar";

const LabBookingDashboard = () => {

  // =========================================
  // STATES
  // =========================================

  const [bookings, setBookings] =
    useState([]);

  const [
    filteredBookings,
    setFilteredBookings,
  ] = useState([]);

  const [
    completedTodayCount,
    setCompletedTodayCount,
  ] = useState(0);

  const [
    todayTestsCount,
    setTodayTestsCount,
  ] = useState(0);

  const [loading, setLoading] =
    useState(true);

  const [search, setSearch] =
    useState("");

  const [activeCard, setActiveCard] =
    useState(null);

  // =========================================
  // RESCHEDULE MODAL
  // =========================================

  const [
    rescheduleModal,
    setRescheduleModal,
  ] = useState(false);

  const [
    selectedBooking,
    setSelectedBooking,
  ] = useState(null);

  const [
    rescheduleData,
    setRescheduleData,
  ] = useState({
    date: "",
    time: "",
  });

  // =========================================
  // FETCH BOOKINGS
  // =========================================

  const fetchBookings = async () => {

    try {

      setLoading(true);

      const response =
        await API.post(
          "/lab/pending-bookings/"
        );

      setBookings(
        response.data.bookings || []
      );

      setFilteredBookings(
        response.data.bookings || []
      );

      setCompletedTodayCount(
        response.data
          .completed_today_count || 0
      );

      setTodayTestsCount(
        response.data
          .today_tests_count || 0
      );

    } catch (error) {

      console.log(error);

    } finally {

      setLoading(false);

    }

  };

  // =========================================
  // INITIAL LOAD
  // =========================================

  useEffect(() => {

    fetchBookings();

  }, []);

  // =========================================
  // SEARCH
  // =========================================

  useEffect(() => {

    const filtered =
      bookings.filter((item) =>
        item.patient_name
          ?.toLowerCase()
          .includes(
            search.toLowerCase()
          )
      );

    setFilteredBookings(
      filtered
    );

  }, [search, bookings]);

  // =========================================
  // ACCEPT / REJECT
  // =========================================

  const handleAction = async (
    booking,
    action
  ) => {

    try {

      setActiveCard(
        booking.id
      );

      const response =
        await API.post(
          "/lab/update-booking-status",
          {
            booking_id:
              booking.id,

            status: action,
          }
        );

      if (
        response.data.status
      ) {

        alert(
          response.data.message
        );

        fetchBookings();

      }

    } catch (error) {

      console.log(error);

      alert(
        "Something went wrong"
      );

    } finally {

      setActiveCard(null);

    }

  };

  // =========================================
  // RESCHEDULE
  // =========================================

  const confirmReschedule =
    async () => {

      try {

        if (
          !rescheduleData.date
        ) {

          alert(
            "Please select date"
          );

          return;

        }

        if (
          !rescheduleData.time
        ) {

          alert(
            "Please select time"
          );

          return;

        }

        setActiveCard(
          selectedBooking.id
        );

        const response =
          await API.post(
            "lab/reschedule-booking",
            {
              booking_id:
                selectedBooking.id,

              date:
                rescheduleData.date,

              time:
                rescheduleData.time,
            }
          );

        if (
          response.data.status
        ) {

          alert(
            response.data.message
          );

          setRescheduleModal(
            false
          );

          fetchBookings();

        }

      } catch (error) {

        console.log(error);

        alert(
          "Failed to reschedule"
        );

      } finally {

        setActiveCard(null);

      }

    };

  // =========================================
  // REFRESH
  // =========================================

  const refreshData = () => {

    fetchBookings();

  };

  // =========================================
  // UI
  // =========================================

  return (

    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100  pb-6">

      <Navbar />

      <div className="pt-1 px-6 pb-10">

        {/* ========================================= */}
        {/* HEADER */}
        {/* ========================================= */}

        <div className="bg-white/80 backdrop-blur-xl border border-white/20 rounded-[32px] p-6 shadow-xl mb-8">

          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">

            {/* LEFT */}

            <div className="flex items-center gap-5">

              <div className="w-16 h-16 rounded-3xl bg-gradient-to-r from-blue-600 to-indigo-600 flex items-center justify-center text-white shadow-xl">

                <FlaskConical size={32} />

              </div>

              <div>

                <h1 className="text-4xl font-black text-slate-800">
                  Lab Booking Requests
                </h1>

                <p className="text-slate-500 mt-1">
                  Manage all patient lab bookings
                </p>

              </div>

            </div>

            {/* RIGHT */}

            <div className="flex items-center gap-4 flex-wrap">

              {/* SEARCH */}

              <div className="relative">

                <Search
                  className="absolute left-4 top-4 text-slate-400"
                  size={18}
                />

                <input
                  type="text"
                  placeholder="Search patient..."
                  value={search}
                  onChange={(e) =>
                    setSearch(
                      e.target.value
                    )
                  }
                  className="pl-11 pr-4 py-3 rounded-2xl border border-slate-200 bg-white shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 w-[280px]"
                />

              </div>

              {/* REFRESH */}

              <button
                onClick={
                  refreshData
                }
                className="w-14 h-14 rounded-2xl bg-gradient-to-r from-blue-600 to-indigo-600 text-white flex items-center justify-center shadow-lg hover:scale-105 transition-all"
              >

                <RefreshCcw size={22} />

              </button>

            </div>

          </div>

        </div>

        {/* ========================================= */}
        {/* STATS */}
        {/* ========================================= */}

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">

          {/* PENDING */}

          <div className="bg-white rounded-[30px] p-6 shadow-xl border border-slate-100">

            <div className="flex items-center justify-between">

              <div>

                <p className="text-slate-500 text-sm font-medium">
                  Pending Bookings
                </p>

                <h2 className="text-5xl font-black text-slate-800 mt-2">
                  {
                    bookings.length
                  }
                </h2>

              </div>

              <div className="w-16 h-16 rounded-3xl bg-yellow-100 flex items-center justify-center text-yellow-600">

                <Bell size={30} />

              </div>

            </div>

          </div>

          {/* COMPLETED */}

          <div className="bg-white rounded-[30px] p-6 shadow-xl border border-slate-100">

            <div className="flex items-center justify-between">

              <div>

                <p className="text-slate-500 text-sm font-medium">
                  Completed Today
                </p>

                <h2 className="text-5xl font-black text-slate-800 mt-2">
                  {
                    completedTodayCount
                  }
                </h2>

              </div>

              <div className="w-16 h-16 rounded-3xl bg-green-100 flex items-center justify-center text-green-600">

                <CheckCircle2 size={30} />

              </div>

            </div>

          </div>

          {/* TODAY */}

          <div className="bg-white rounded-[30px] p-6 shadow-xl border border-slate-100">

            <div className="flex items-center justify-between">

              <div>

                <p className="text-slate-500 text-sm font-medium">
                  Today's Tests
                </p>

                <h2 className="text-5xl font-black text-slate-800 mt-2">
                  {
                    todayTestsCount
                  }
                </h2>

              </div>

              <div className="w-16 h-16 rounded-3xl bg-blue-100 flex items-center justify-center text-blue-600">

                <CalendarDays size={30} />

              </div>

            </div>

          </div>

        </div>

        {/* ========================================= */}
        {/* LOADING */}
        {/* ========================================= */}

        {loading ? (

          <div className="flex items-center justify-center h-[500px]">

            <div className="w-20 h-20 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>

          </div>

        ) : (

          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-7">

            {filteredBookings.map(
              (booking) => (

                <div
                  key={
                    booking.id
                  }
                  className="bg-white rounded-[36px] p-6 shadow-xl border border-slate-100 hover:-translate-y-1 hover:shadow-2xl transition-all duration-300"
                >

                  {/* TOP */}

                  <div className="flex items-start justify-between mb-6">

                    <div className="flex items-center gap-4">

                      {/* AVATAR */}

                      <div className="w-16 h-16 rounded-3xl bg-gradient-to-r from-blue-600 to-indigo-600 flex items-center justify-center text-white text-xl font-black shadow-xl">

                        {booking.patient_name
                          ?.slice(0, 2)
                          ?.toUpperCase()}

                      </div>

                      <div>

                        <h2 className="text-2xl font-black text-slate-800">
                          {
                            booking.patient_name
                          }
                        </h2>

                        <p className="text-slate-500">
                          Lab Booking Request
                        </p>

                        <div className="mt-2">

                          <span
                            className={`
                              px-3
                              py-1
                              rounded-full
                              text-xs
                              font-bold
                              ${
                                booking.test_type ===
                                "At home"
                                  ? "bg-green-100 text-green-700"
                                  : "bg-blue-100 text-blue-700"
                              }
                            `}
                          >
                            {
                              booking.test_type
                            }
                          </span>

                        </div>

                      </div>

                    </div>

                    <div className="px-4 py-2 rounded-full bg-yellow-100 text-yellow-700 text-xs font-bold">

                      Pending

                    </div>

                  </div>

                  {/* INFO */}

                  <div className="space-y-4 mb-6">

                    {/* DATE */}

                    <div className="bg-slate-50 rounded-2xl p-4 flex items-center justify-between">

                      <div className="flex items-center gap-3 text-slate-600">

                        <CalendarDays size={18} />

                        <span>Date</span>

                      </div>

                      <span className="font-bold text-slate-800">
                        {
                          booking.date
                        }
                      </span>

                    </div>

                    {/* TIME */}

                    <div className="bg-slate-50 rounded-2xl p-4 flex items-center justify-between">

                      <div className="flex items-center gap-3 text-slate-600">

                        <Clock3 size={18} />

                        <span>Time</span>

                      </div>

                      <span className="font-bold text-slate-800">
                        {
                          booking.time
                        }
                      </span>

                    </div>

                    {/* PATIENT */}

                    <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-3xl p-5 border border-blue-100">

                      <div className="flex items-center gap-3 mb-3">

                        <User
                          size={18}
                          className="text-blue-600"
                        />

                        <span className="font-bold text-blue-700">
                          Patient Information
                        </span>

                      </div>

                      <div className="space-y-2 text-sm text-slate-700">

                        <p>
                          Name:
                          {" "}
                          {
                            booking.patient_name
                          }
                        </p>

                        <div className="flex items-center gap-2">

                          <Phone
                            size={14}
                          />

                          <span>
                            {
                              booking.patient_number
                            }
                          </span>

                        </div>

                      </div>

                    </div>

                    {/* TESTS */}

                    <div className="bg-gradient-to-r from-indigo-50 to-blue-50 rounded-3xl p-5 border border-indigo-100">

                      <div className="flex items-center gap-3 mb-3">

                        <TestTube2
                          size={18}
                          className="text-indigo-600"
                        />

                        <span className="font-bold text-indigo-700">
                          Selected Tests
                        </span>

                      </div>

                      <div className="flex flex-wrap gap-2">

                        {booking.test_names &&
                        booking.test_names.length > 0 ? (

                          booking.test_names.map(
                            (test, index) => (

                              <div
                                key={index}
                                className="
                                  px-3
                                  py-2
                                  rounded-xl
                                  bg-white
                                  border
                                  border-indigo-200
                                  text-indigo-700
                                  text-sm
                                  font-semibold
                                  shadow-sm
                                "
                              >
                                {test}
                              </div>

                            )
                          )

                        ) : (

                          <p className="text-sm text-slate-500">
                            No tests available
                          </p>

                        )}

                      </div>

                    </div>

                    {/* LOCATION */}

                    <div className="bg-gradient-to-r from-emerald-50 to-green-50 rounded-3xl p-5 border border-emerald-100">

                      <div className="flex items-center gap-3">

                        {booking.test_type ===
                        "At home" ? (

                          <Home
                            size={18}
                            className="text-emerald-600"
                          />

                        ) : (

                          <Building2
                            size={18}
                            className="text-emerald-600"
                          />

                        )}

                        <span className="font-bold text-emerald-700">
                          {
                            booking.test_type ===
                            "At home"
                              ? "Home Collection"
                              : "Lab Visit"
                          }
                        </span>

                      </div>

                    </div>

                  </div>

                  {/* BUTTONS */}

                  <div className="grid grid-cols-3 gap-3">

                    {/* ACCEPT */}

                    <button
                      onClick={() =>
                        handleAction(
                          booking,
                          "confirmed"
                        )
                      }
                      disabled={
                        activeCard ===
                        booking.id
                      }
                      className="bg-gradient-to-r from-green-500 to-emerald-500 text-white py-3 rounded-2xl font-bold shadow-lg hover:scale-105 transition-all"
                    >

                      {
                        activeCard ===
                        booking.id
                          ? "..."
                          : "Accept"
                      }

                    </button>

                    {/* RESCHEDULE */}

                    <button
                      onClick={() => {

                        setSelectedBooking(
                          booking
                        );

                        setRescheduleModal(
                          true
                        );

                        setRescheduleData(
                          {
                            date:
                              booking.date,

                            time:
                              booking.time,
                          }
                        );

                      }}
                      className="bg-gradient-to-r from-blue-500 to-indigo-500 text-white py-3 rounded-2xl font-bold shadow-lg hover:scale-105 transition-all"
                    >

                      Reschedule

                    </button>

                    {/* REJECT */}

                    <button
                      onClick={() =>
                        handleAction(
                          booking,
                          "rejected"
                        )
                      }
                      className="bg-gradient-to-r from-red-500 to-rose-500 text-white py-3 rounded-2xl font-bold shadow-lg hover:scale-105 transition-all"
                    >

                      Reject

                    </button>

                  </div>

                </div>

              )
            )}

          </div>

        )}

      </div>
      {/* ========================================= */}
{/* RESCHEDULE MODAL */}
{/* ========================================= */}

{rescheduleModal && (

  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-md p-4 animate-fadeIn">

    <div className="bg-white w-full max-w-xl rounded-[40px] shadow-2xl overflow-hidden border border-blue-100">

      {/* HEADER */}

      <div className="relative bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 p-8 text-white overflow-hidden">

        {/* Glow */}

        <div className="absolute -top-10 -right-10 w-40 h-40 bg-white/10 rounded-full blur-3xl"></div>

        <div className="relative flex items-center justify-between">

          <div>

            <div className="flex items-center gap-3 mb-2">

              <div className="w-14 h-14 rounded-2xl bg-white/20 backdrop-blur-lg flex items-center justify-center shadow-lg">

                <RefreshCcw size={28} />

              </div>

              <div>

                <h2 className="text-3xl font-black">
                  Reschedule Lab Booking
                </h2>

                <p className="text-blue-100 mt-1">
                  Choose a new date and time slot
                </p>

              </div>

            </div>

          </div>

          {/* CLOSE */}

          <button
            onClick={() =>
              setRescheduleModal(false)
            }
            className="
              w-12
              h-12
              rounded-2xl
              bg-white/20
              hover:bg-white/30
              transition-all
              flex
              items-center
              justify-center
            "
          >

            <XCircle size={28} />

          </button>

        </div>

      </div>

      {/* BODY */}

      <div className="p-8">

        {/* PATIENT CARD */}

        <div className="bg-gradient-to-r from-slate-50 to-blue-50 rounded-3xl p-5 border border-slate-200 mb-8">

          <div className="flex items-center gap-4">

            {/* AVATAR */}

            <div className="w-16 h-16 rounded-3xl bg-gradient-to-r from-blue-600 to-indigo-600 text-white flex items-center justify-center text-xl font-black shadow-lg">

              {selectedBooking?.patient_name
                ?.slice(0, 2)
                ?.toUpperCase()}

            </div>

            {/* INFO */}

            <div>

              <h3 className="text-xl font-black text-slate-800">
                {
                  selectedBooking?.patient_name
                }
              </h3>

              <div className="flex items-center gap-2 text-slate-500 mt-1">

                <Phone size={14} />

                <span>
                  {
                    selectedBooking?.patient_number
                  }
                </span>

              </div>

              <div className="mt-2">

                <span
                  className={`
                    px-3
                    py-1
                    rounded-full
                    text-xs
                    font-bold
                    ${
                      selectedBooking?.test_type ===
                      "At home"
                        ? "bg-green-100 text-green-700"
                        : "bg-blue-100 text-blue-700"
                    }
                  `}
                >
                  {
                    selectedBooking?.test_type
                  }
                </span>

              </div>

            </div>

          </div>

        </div>

        {/* DATE */}

        <div className="mb-6">

          <label className="block text-slate-700 font-bold mb-3 text-lg">

            Select Date

          </label>

          <div className="relative">

            <CalendarDays
              className="absolute left-5 top-5 text-slate-400"
              size={20}
            />

            <input
              type="date"
              value={
                rescheduleData.date
              }
              min={
                new Date()
                  .toISOString()
                  .split("T")[0]
              }
              onChange={(e) =>
                setRescheduleData({
                  ...rescheduleData,
                  date:
                    e.target.value,
                })
              }
              className="
                w-full
                bg-slate-50
                border
                border-slate-200
                rounded-3xl
                pl-14
                pr-5
                py-5
                outline-none
                text-lg
                font-semibold
                focus:ring-2
                focus:ring-blue-500
                focus:border-transparent
                transition-all
              "
            />

          </div>

        </div>

        {/* TIME */}

        <div>

          <label className="block text-slate-700 font-bold mb-3 text-lg">

            Select Time

          </label>

          <div className="relative">

            <Clock3
              className="absolute left-5 top-5 text-slate-400"
              size={20}
            />

            <input
              type="time"
              value={
                rescheduleData.time
              }
              onChange={(e) =>
                setRescheduleData({
                  ...rescheduleData,
                  time:
                    e.target.value,
                })
              }
              className="
                w-full
                bg-slate-50
                border
                border-slate-200
                rounded-3xl
                pl-14
                pr-5
                py-5
                outline-none
                text-lg
                font-semibold
                focus:ring-2
                focus:ring-blue-500
                focus:border-transparent
                transition-all
              "
            />

          </div>

        </div>

        {/* TESTS */}

        {selectedBooking?.test_names
          ?.length > 0 && (

          <div className="mt-8">

            <div className="flex items-center gap-3 mb-4">

              <TestTube2
                size={18}
                className="text-indigo-600"
              />

              <h4 className="font-black text-slate-800 text-lg">
                Selected Tests
              </h4>

            </div>

            <div className="flex flex-wrap gap-3">

              {selectedBooking.test_names.map(
                (
                  test,
                  index
                ) => (

                  <div
                    key={index}
                    className="
                      px-4
                      py-2
                      rounded-2xl
                      bg-indigo-50
                      border
                      border-indigo-200
                      text-indigo-700
                      font-semibold
                      shadow-sm
                    "
                  >
                    {test}
                  </div>

                )
              )}

            </div>

          </div>

        )}

        {/* BUTTONS */}

        <div className="grid grid-cols-2 gap-4 mt-10">

          {/* CANCEL */}

          <button
            onClick={() =>
              setRescheduleModal(
                false
              )
            }
            className="
              bg-slate-100
              hover:bg-slate-200
              text-slate-700
              py-4
              rounded-3xl
              font-black
              text-lg
              transition-all
            "
          >

            Cancel

          </button>

          {/* CONFIRM */}

          <button
            onClick={
              confirmReschedule
            }
            disabled={
              activeCard ===
              selectedBooking?.id
            }
            className="
              bg-gradient-to-r
              from-blue-600
              via-indigo-600
              to-purple-600
              text-white
              py-4
              rounded-3xl
              font-black
              text-lg
              shadow-2xl
              hover:scale-105
              active:scale-95
              transition-all
              duration-300
              flex
              items-center
              justify-center
              gap-3
            "
          >

            <RefreshCcw size={20} />

            {
              activeCard ===
              selectedBooking?.id
                ? "Updating..."
                : "Confirm Changes"
            }

          </button>

        </div>

      </div>

    </div>

  </div>

)}

    </div>

  );

};

export default LabBookingDashboard;